/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";
import { GET_PRESENTATION } from "../graphql/getPresentation.query";

const is = require("is_js");

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IGetPresentationMethods extends IPluginMethodMap {
  getPresentation(args: any): Promise<any>;
}

export class GetPresentationService implements IAgentPlugin {
  readonly methods: IGetPresentationMethods;

  constructor() {
    this.methods = {
      getPresentation: this.getPresentation.bind(this),
    };
  }

  async getPresentation(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const res = await client.query({
          query: GET_PRESENTATION,
          variables: {
            filter: {
              _id: {
                eq: args,
              },
              status: {
                eq: "active",
              },
            },
          },
          fetchPolicy: "network-only",
        });

        if (
          res?.data?.getPresentations?.content &&
          is.not.empty(res?.data?.getPresentations?.content)
        ) {
          let sectionArray: any = [];

          for (let obj of res?.data?.getPresentations?.content) {
            if (obj && is.not.empty(obj)) {
              const payload = this.formatData(obj);
              if (payload && is.not.empty(payload)) {
                sectionArray.push(...payload);
              }
            }
          }

          if (sectionArray && sectionArray.length > 0) {
            sectionArray.unshift({
              name: "...",
              id: "",
              costumeTypes: [
                { name: "...", id: "", partition: [{ name: "...", id: "" }] },
              ],
            });
          }

          return { sectionList: sectionArray };
        } else {
          return [];
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private formatData(data: any) {
    let sectionList: any = [];

    for (let obj of data?.sections) {
      if (obj && is.not.empty(obj)) {
        let costumeList: any = [
          { name: "...", id: "", partition: [{ name: "...", id: "" }] },
        ];

        for (let costumeObj of obj?.costumeTypes) {
          let partitionList: any = [{ name: "...", id: "" }];

          if (costumeObj && is.not.empty(costumeObj)) {
            if (
              costumeObj?.inventoryDetail?.defaultInvitesRemaining &&
              costumeObj?.inventoryDetail?.defaultInvitesRemaining > 0 &&
              !partitionList.some((e: any) => e.id === "Default")
            ) {
              partitionList.push({
                name: "Default Invite",
                id: "Default",
              });
            }

            if (
              costumeObj?.inventoryDetail?.sectionSpecificInvitesRemaining &&
              costumeObj?.inventoryDetail?.sectionSpecificInvitesRemaining >
                0 &&
              !partitionList.some((e: any) => e.id === "SectionSpecific")
            ) {
              partitionList.push({
                name: "Section Specific Invite",
                id: "SectionSpecific",
              });
            }

            if (
              costumeObj?.inventoryDetail?.specialInvitesRemaining &&
              costumeObj?.inventoryDetail?.specialInvitesRemaining > 0 &&
              !partitionList.some((e: any) => e.id === "SpecialReserve")
            ) {
              partitionList.push({
                name: "Special Invite",
                id: "SpecialReserve",
              });
            }

            costumeObj.partition = partitionList;

            if (costumeObj?.isVisible) {
              costumeList.push(costumeObj);
            }
          }
        }
        obj.costumeTypes = costumeList;

        if (obj?.isVisible) {
          sectionList.push(obj);
        }
      }
    }

    return sectionList;
  }
}
